<template>
    <common-content-spacer height="medium">
        <div class="category-info">
            <div v-if="title" class="category-info--title">{{ title }}</div>
            <div class="category-info--search">
                <offer-category-filter-form
                    v-if="offer.offerCategories.length > 3 && !showMore"
                    :prefill="filterApplied"
                    @search-category-change="filterCategory($event)"
                ></offer-category-filter-form>
            </div>
            <div class="category-info__user-selector">
                <common-user-selector
                    v-if="availabilityUserIds.length > 1 && showPerformersFilter"
                    :users="availabilityPerformers"
                    :selectedUserId="selectedPerformerId"
                    @user-selected="userSelected($event)"
                ></common-user-selector>
            </div>
        </div>
        <div :class="categoryContainerClass">
            <div v-for="category in getOfferCategories" :key="category.id" :class="categoryWrapperClass(category)">
                <checkbox-input
                    v-if="allowSelection"
                    class="category-select--checkbox"
                    type="checkbox"
                    :isDisabled="category.disabled"
                    :iconSize="iconSize"
                    labelPosition="left"
                    :value="category.isSelected"
                    @change="categorySelected(category)"
                ></checkbox-input>
                <div :class="categorySelectClass" @click="categorySelected(category)">
                    <div class="category-select--container">
                        <div class="category-select__title">
                            <p class="category-select__title--text">
                                {{ getTranslation(category?.titleTextContent) }}
                            </p>
                            <p class="category-select__title--appointment">
                                <span class="category-select__title--length">
                                    {{ getAppointmentLengthLabel(category.appointmentLength) }}
                                </span>
                                <span class="category-select__title--details" @click.stop="openCategoryPreviewModal(category)">
                                    {{ $t('common.microcopy.details') }}
                                </span>
                            </p>
                        </div>
                        <div :class="categoryPriceClass">
                            <p>
                                <span v-if="category?.linkedDiscounts?.length" class="category-select__price--discount">
                                    -{{ category.linkedDiscounts[0].discountPercentage * 100 }}%
                                </span>
                                {{ categoryDiscountPrice(category) }}
                            </p>
                        </div>
                    </div>
                    <div class="category-select__description">
                        {{ getTranslation(category?.descriptionTextContent) }}
                    </div>
                </div>
            </div>
            <div v-if="filteredCategories.length === 0 && searchTerm" class="category-select__no-result">
                {{ $t('pages.offer.create_offer.no_search_result') }}
            </div>
        </div>
        <common-buttons v-if="showMore">
            <common-button type="secondary" :label="showMoreTitle" @click="showMoreCategories"></common-button>
        </common-buttons>
        <div v-show="isCategorySelected && allowSelection" class="category-select--info">
            <div class="category-select--data">
                <p class="category-select--info--number">
                    {{ $t('pages.offer.create_offer.selected_category_number', { number: isCategorySelected }) }}
                </p>
                <p v-if="allowSelection" class="category-select--price">
                    <span :class="categoryTotalPriceClass">{{ categoryTotalPrice }}</span>
                    <span class="category-select--price__appointment">{{ getTotalTime }}</span>
                </p>
            </div>
            <common-content-spacer height="medium">
                <common-buttons v-if="!showMore" align="right">
                    <common-button
                        :isDisabled="!isCategorySelected || !getTotalTimeValue"
                        type="tertiary"
                        :label="$t('common.microcopy.choose_time')"
                        @click="goToSchedule"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </div>
        <category-preview-modal
            v-if="isCategoryPreviewModalOpen"
            :category="categoryModalData"
            :offer="offer"
            @close="closeCategoryPreviewModal"
        ></category-preview-modal>
    </common-content-spacer>
</template>

<script>
    import PriceMixin from 'mixins/PriceMixin'
    import AppointmentLengthEnum from 'enums/appointment_length.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import CheckboxInput from 'components/form-inputs/CheckboxInput.vue'
    import CategoryPreviewModal from 'components/modals/offer/CategoryPreviewModal'
    import TranslateMixin from 'mixins/TranslateMixin'
    import AvailabilityUserRoleEnum from 'enums/availability_user_role.enum'
    import OfferCategoryFilterForm from 'components/forms/offer/OfferCategoryFilterForm'

    export default {
        mixins: [PriceMixin, WindowResizeMixin, TranslateMixin],
        components: { CheckboxInput, CategoryPreviewModal, OfferCategoryFilterForm },
        props: {
            offer: { type: Object, required: true },
            showMore: { type: Boolean, default: false },
            showMoreTitle: { type: String, default: undefined },
            showFullDescription: { type: Boolean, default: false },
            title: { type: String, default: undefined },
            allowSelection: { type: Boolean, default: false },
        },
        data() {
            return {
                categoryModalData: null,
                isCategoryPreviewModalOpen: false,
                availabilityUserIds: [],
                filteredCategories: [],
                availabilityPerformers: [],
                selectedPerformerId: undefined,
                searchTerm: undefined,
                showPerformersFilter: undefined,
            }
        },
        computed: {
            categorySelectClass() {
                return {
                    'category-select': true,
                    'category-select--allowselection': this.allowSelection,
                }
            },

            categoryPriceClass() {
                return {
                    'category-select__price': true,
                    'category-select__price--hidden': this.offer.hidePrice,
                }
            },

            categoryTotalPriceClass() {
                return {
                    'category-select--price__value': true,
                    'category-select--price__hidden': this.offer.hidePrice,
                }
            },

            categoryTotalPrice() {
                if (this.offer.hidePrice) return this.offer.currency
                return `${this.getTotalPrice} ${this.offer.currency}`
            },

            iconSize() {
                return this.isSmartphoneLarge ? 'medium' : 'extra-huge'
            },

            categoryContainerClass() {
                return {
                    'category-container': true,
                    'category-container--category-selected': this.allowSelection && this.isCategorySelected,
                }
            },

            getOfferCategories() {
                const categories =
                    !this.searchTerm && !this.selectedPerformerId ? this.offer.offerCategories : this.filteredCategories

                return this.showMore ? categories.slice(0, 3) : categories
            },

            isCategorySelected() {
                return this.offer.offerCategories?.filter((category) => category.isSelected).length
            },

            getTotalPrice() {
                return (
                    this.offer?.offerCategories
                        ?.filter((category) => category.isSelected)
                        .reduce((acc, curr) => acc + curr[`discountedPrice${this.offer.currency}`], 0) / 100
                )
            },

            getTotalTimeValue() {
                const totalTime = this.offer?.offerCategories
                    ?.filter((category) => category.isSelected)
                    .reduce((acc, curr) => acc + curr.appointmentLength, 0)

                return totalTime
            },

            getTotalTime() {
                const totalTime = this.offer?.offerCategories
                    ?.filter((category) => category.isSelected)
                    .reduce((acc, curr) => acc + curr.appointmentLength, 0)

                const hours = Math.floor(totalTime / 60)
                const remainingMinutes = totalTime % 60
                if (hours === 0) {
                    return remainingMinutes + 'm'
                } else if (remainingMinutes === 0) {
                    return hours + 'h'
                } else {
                    return hours + 'h ' + remainingMinutes + 'm'
                }
            },

            getDescriptionClasses() {
                return {
                    'category-select__description': true,
                    'category-select__description--elipsis': !this.showFullDescription,
                }
            },
        },
        methods: {
            filterCategory(value) {
                this.searchTerm = value ? value : undefined
                this.searchByParams(this.searchTerm, this.selectedPerformerId)
            },

            categoryPrice(category) {
                let price = this.setPrice(category[`price${this.offer.currency}`])

                return `${this.offer.currency} : ${price}`
            },

            openCategoryPreviewModal(category) {
                this.categoryModalData = category
                this.isCategoryPreviewModalOpen = true
            },

            closeCategoryPreviewModal() {
                this.categoryModalData = null
                this.isCategoryPreviewModalOpen = false
            },

            categoryDiscountPrice(category) {
                if (this.offer.hidePrice) {
                    return `${this.offer.currency}`
                }
                let price = this.setPrice(category[`discountedPrice${this.offer.currency}`])

                return `${this.offer.currency} : ${price}`
            },

            getAppointmentLengthLabel(value) {
                return AppointmentLengthEnum[this.$i18n.locale].find((option) => Number(option.value) === value)?.label
            },

            showMoreCategories() {
                this.emitEvent({ name: 'check-availability' })
            },

            makeCategoriesDisabled() {
                const excludedUsers = this.getOfferCategories
                    .filter((oc) => oc.isSelected)
                    .map((oc) => oc.excludeUsers || [])
                    .flat()

                this.getOfferCategories = this.getOfferCategories.map((oc) => {
                    if (oc.isSelected) {
                        oc.disabled = false
                    } else if (
                        this.availabilityUserIds.every((userId) =>
                            [...excludedUsers, ...(oc.excludeUsers || [])].includes(userId),
                        )
                    ) {
                        oc.disabled = true
                    } else {
                        oc.disabled = false
                    }
                    return oc
                })
            },

            categorySelected(category) {
                if (category.disabled) {
                    return
                }
                if (this.allowSelection) {
                    category.isSelected = !category.isSelected
                }
                const payload = this.offer.offerCategories.filter((category) => category.isSelected)
                this.emitEvent({ name: 'categories-change', payload })

                this.makeCategoriesDisabled()

                const el = document.getElementsByClassName('category-select--data')[0]
                el.classList.add('apply-scale')
                el.addEventListener('animationend', () => {
                    el.classList.remove('apply-scale')
                })
            },

            deselectCategories() {
                this.getOfferCategories = this.getOfferCategories.map((oc) => {
                    oc.isSelected = false
                    oc.disabled = false
                    return oc
                })
            },

            goToSchedule() {
                const payload = this.offer.offerCategories.filter((category) => category.isSelected)
                this.emitEvent({ name: 'categories-select', payload })
            },

            categoryWrapperClass(category) {
                return {
                    'category-select--wrapper': true,
                    'category-select--wrapper--disabled': category.disabled,
                }
            },
            userSelected(userId) {
                this.deselectCategories()

                if (this.selectedPerformerId !== userId) {
                    this.selectedPerformerId = userId

                    this.searchByParams(this.searchTerm, userId)
                } else {
                    this.selectedPerformerId = undefined

                    if (this.searchTerm) {
                        this.filterCategory(this.searchTerm)
                    } else {
                        this.filteredCategories = this.getOfferCategories
                    }
                }
            },

            searchByParams(searchTerm, excludedUserId) {
                this.filteredCategories = this.offer.offerCategories.filter((category) => {
                    const title = this.getTranslation(category.titleTextContent).toLowerCase()
                    const description = this.getTranslation(category.descriptionTextContent).toLowerCase()

                    let matchesSearch = true
                    let matchesExclusion = true

                    if (searchTerm) {
                        const searchValue = searchTerm.toLowerCase()
                        matchesSearch = title.includes(searchValue) || description.includes(searchValue)
                    }

                    if (excludedUserId) {
                        matchesExclusion = !category.excludeUsers?.includes(excludedUserId)
                    }

                    return matchesSearch && matchesExclusion
                })
            },
        },

        mounted() {
            this.availabilityUserIds = Array.from(
                new Set(
                    this.offer.availabilityOffers
                        .map((ao) =>
                            ao.availability.availabilityUsers
                                .filter((au) => au.function === AvailabilityUserRoleEnum.PERFORMER)
                                .map((a) => a.user.id),
                        )
                        .flat(),
                ),
            )

            const tempPerformerIds = []
            this.offer.availabilityOffers.map((ao) => {
                ao.availability.availabilityUsers.map((au) => {
                    if (!tempPerformerIds.includes(au.user.id)) {
                        this.availabilityPerformers.push(au.user)
                        tempPerformerIds.push(au.user.id)
                    }
                })
            })

            this.showPerformersFilter = this.getOfferCategories.some((oc) => oc.excludeUsers?.length > 0)

            this.getOfferCategories = this.getOfferCategories.map((oc) => {
                if (this.availabilityUserIds.every((userId) => (oc.excludeUsers || []).includes(userId))) {
                    oc.disabled = true
                }
                return oc
            })

            this.makeCategoriesDisabled()
        },
    }
</script>
<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/mixins';

    .tab__content {
        padding-bottom: 0;
    }

    .category-info {
        display: flex;
        align-items: center;
        justify-content: space-between;
        @include breakpoint(tablet) {
            flex-direction: column;
        }
        &--title {
            font-weight: 500;
            margin-bottom: 10px;
            font-size: 20px;
            @include breakpoint(tablet) {
                margin-left: 10px;
                margin-right: 10px;
            }
        }
        &--search {
            font-weight: 500;
            margin-bottom: 10px;
            font-size: 20px;
            min-width: 300px;
        }

        &__user-selector {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-left: 10px;

            @media screen and (max-width: 1025px) {
                padding-top: 10px;
            }
        }
    }

    .category-container {
        padding-bottom: 15px;

        &--category-selected {
            @include breakpoint(smartphone-large) {
                margin-bottom: 120px;
            }
        }

        &--category-disabled {
            opacity: 0.4;
            cursor: not-allowed !important;
        }
    }

    .category-select--wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid $color--border-base;

        &--disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    .category-select {
        margin-bottom: 10px;
        width: 100%;
        padding: 10px;

        &--allowselection {
            max-width: calc(100% - 80px);

            cursor: pointer;
            @include breakpoint(tablet-small) {
                cursor: none;
            }

            @include breakpoint(smartphone-large) {
                max-width: calc(100% - 40px);
            }
        }

        &--info {
            @include breakpoint(smartphone-large) {
                flex-wrap: wrap;
                justify-content: center;
                text-align: center;
                .button {
                    margin-top: 20px;
                }
            }
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: fixed;
            bottom: 0;
            right: 0;
            z-index: $layer--above-parent;
            width: 100%;
            padding-top: 50px;
            padding: 10px 80px;
            background-color: $color--bg-dropdown;
            box-shadow: 2px 2px 8px 0 rgba(#000, 0.3);
            transition: all 0.3s ease-in-out;

            &--number {
                font-size: 16px;
            }
        }

        &--price {
            font-weight: 500;
            color: $color--txt-header-3;
            font-size: 18px;
            &__appointment {
                color: $color--txt-header-3;
                margin-left: 20px;
            }
            &__hidden {
                @include hiddenBlur();
            }
        }

        &--container {
            display: flex;
            justify-content: space-between;

            @include breakpoint(smartphone-large) {
                flex-wrap: wrap;
            }
        }

        &__title {
            @include ellipsis();

            &--text {
                @include ellipsis();
                font-weight: 600;
            }

            &--length {
                margin-right: 10px;
            }

            &--appointment {
                display: flex;
                flex-wrap: wrap;
                color: $color--txt-base;
            }

            &--details {
                color: $color-link;
                cursor: pointer;
            }
        }

        &__description {
            color: $color--txt-base-3;
            @include ellipsis();
        }
        &__no-result {
            text-align: center;
        }

        &__price {
            font-weight: 600;
            text-align: right;

            @include breakpoint(tablet) {
                margin: 10px 0;
            }

            &--discount {
                font-weight: 400;
                background-color: $color-gold;
                border-radius: 10px;
                padding: 5px;
                margin-right: 10px;
            }

            &--currency {
                font-weight: 400;
                font-size: 12px;
            }

            &--original {
                text-decoration: line-through;
                opacity: 0.5;
                font-size: 10px;
                font-weight: 400;
                vertical-align: super;
                font-size: smaller;
            }
            &--hidden {
                @include hiddenBlur();
            }
        }
    }

    .subdomain-layout {
        .category-select {
            &--price {
                &__value {
                    color: $subdomain-almost-black1;
                }
            }

            &--right {
                @include breakpoint(tablet) {
                    background-color: $subdomain-midnight;
                }
            }
        }
        &--navy_black,
        &--black {
            .category-select--wrapper {
                border-color: $subdomain-slate1;
            }
            .category-info {
                color: $color-light-grayish-blue-2;
            }
            .category-select {
                &__title {
                    color: $subdomain-very-light1;
                }

                &__description {
                    color: $subdomain-light1;
                }

                &__no-result {
                    color: $subdomain-light1;
                }

                &--price {
                    &__value {
                        color: $subdomain-blue;
                    }

                    &__appointment {
                        color: $subdomain-blue;
                    }
                }

                &--info {
                    background-color: $subdomain-charcoal2;
                    color: $color-white;
                }
            }
        }
    }
</style>
